import { ButtonSecondary, InputMessage, typographyRem } from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import React, { useContext, useEffect, useState } from 'react'

import { AddressInput } from '../../../../generated/graphql'
import { useQuote } from '../../../../hooks/useQuote'
import { useDriverPageContext } from '../../../../pages/DriverPageProvider'
import { usePreQuoteContext } from '../../../../pages/PageValidator'
import { ProviderContext } from '../../../../Provider'
import { formatPostcode } from '../../../../utils/formatPostcode'
import { FindMyAddress } from '../../../FindMyAddress'
import { GenericSectionStyle } from '../../GenericSectionStyle'
import { EnterAddressManually } from './EnterAddressManually'
const { h4: H4, h5: H5 } = typographyRem

export const DriverHomeAddress = () => {
  const quote = useQuote()
  const { showErrorBanner, updateValidInputs, validInputs } = usePreQuoteContext()
  const {
    state: { selectedDriverIndex, showModal },
  } = useContext(ProviderContext)
  const { updatedDriverDetails, setUpdatedDriverDetails } = useDriverPageContext()

  const [address, setAddress] = useState<AddressInput>({
    line_1: quote?.details?.proposer?.address?.line_1,
    line_2: quote?.details?.proposer?.address?.line_2,
    line_3: quote?.details?.proposer?.address?.line_3,
    city: quote?.details?.proposer?.address?.city,
    postcode: quote?.details?.proposer?.address?.postcode,
    country: quote?.details?.proposer?.address?.country,
    county: quote?.details?.proposer?.address?.county,
  })
  const [enterManually, setEnterManually] = useState<boolean>(false)
  const emptyAddress = !address.line_1 && !address.city && !address.postcode

  const addressError = showErrorBanner && !validInputs[selectedDriverIndex]?.address

  const handleSetAddress = async (updatedAddress: AddressInput) => {
    if (JSON.stringify(address) !== JSON.stringify(updatedAddress)) {
      setAddress(updatedAddress)
      setUpdatedDriverDetails({ ...updatedDriverDetails, address: updatedAddress })
    }
  }

  useEffect(() => {
    const validAddress = !!address?.postcode && !!address?.city && !!address?.line_1
    if (Object.values(address).some(v => !!v) && !validAddress) {
      setEnterManually(true)
    }
    updateValidInputs({ field: 'address', value: validAddress, index: selectedDriverIndex })
  }, [address])

  return (
    <>
      <H4>Your home address.</H4>
      <GenericSectionStyle>
        <div className="generic-wrapper">
          <div className="question-container" id="address">
            {emptyAddress && !enterManually && (
              <FindMyAddress
                dataCy="home-address"
                manualEdit={() => setEnterManually(true)}
                setAddress={value => {
                  DataLayer.customEvent({
                    eventName: 'home address',
                    section: 'driver',
                    eventType: 'form tracking',
                  })
                  handleSetAddress(value)
                }}
                showConfirmButtons={false}
                showCantFindAddressLink={false}
                cancel={() => {}}
                confirm={() => {}}
                toopTip="This must be your legally registered home address (where you live). If you want us to send any packages to another address (like your work) you can let us know when you buy the policy."
              />
            )}
            {!emptyAddress && !enterManually && (
              <>
                <div className="address-text-container">
                  <H5>{address?.line_1}</H5>
                  <H5>{address?.line_2}</H5>
                  <H5>{address?.line_3}</H5>
                  <H5>{address?.city}</H5>
                  <H5>{address?.county}</H5>
                  <H5>{address?.postcode && formatPostcode(address.postcode)}</H5>
                </div>
                <ButtonSecondary
                  type="button"
                  className="edit LoNotSensitive"
                  label="Edit"
                  icon
                  onClick={() => setEnterManually(true)}
                  dataCy="edit-address-button"
                  tabFocusTrap={showModal ? -1 : 0}
                />
              </>
            )}
            {enterManually && (
              <EnterAddressManually
                onChange={value => {
                  DataLayer.customEvent({
                    eventName: 'typed home address',
                    section: 'driver',
                    eventType: 'form tracking',
                  })
                  handleSetAddress(value)
                }}
                findDifferentAddress={() => {
                  setAddress({})
                  setEnterManually(false)
                }}
                preFilled={address}
                error={addressError}
                showModal={showModal}
              />
            )}
            {addressError && (
              <InputMessage
                position="bottom"
                message={
                  enterManually
                    ? 'Please fill in the required fields to continue.'
                    : 'Please enter your postcode and select your address from the list to continue.'
                }
                hideArrow
                className="page-level-error"
              />
            )}
          </div>
        </div>
      </GenericSectionStyle>
    </>
  )
}
